/**
 * shared/voice.js - the platform's praise, and the three pools that are not
 * praise.
 *
 * WHY THIS IS NOT A GAME'S FILE, discovered by building the second game.
 *
 * Section 8.5 requires "100+ unique praise phrases per game" and forbids
 * repeating one twice in a row. Read literally, the hundredth game on this
 * platform needs its own hundred phrases, and the ten thousandth phrase will be
 * a synonym of the ninetieth written by somebody who never read the other nine
 * thousand. That is not a rule anybody can keep, and a rule nobody keeps
 * protects nobody - least of all a child on the autism spectrum, for whom
 * section 2.3 says repetition is the actual harm.
 *
 * What the rule is really asking for is that a CHILD does not hear the same
 * line twice, and a child plays several games. So the pools are the platform's,
 * and a game adds the lines only it can say - "one bubble down" belongs to
 * Bubble Pop and would be nonsense anywhere else.
 *
 *   mergePools(SHARED, gameLines)  ->  one pool, deduplicated
 *
 * FOUR POOLS, NOT ONE. Praise belongs to a correct production. A child who
 * vocalised without producing a word has done something real and is
 * acknowledged, not praised; a child who said a different word is answered;
 * a child out of tries is helped. Mixing them teaches a child that the praise
 * means nothing, which is how a reinforcer dies.
 *
 * NO SLANG. It dates, it varies by region, and it is one more thing to decode
 * for a child working hard on decoding.
 */

/** @typedef {{text:string, tone:'bright'|'calm'}} Line */

/** Spoken when the child produced the target. */
export const PRAISE = [
  { text: 'That is exactly it.', tone: 'bright' },
  { text: 'You said it.', tone: 'calm' },
  { text: 'Perfect.', tone: 'bright' },
  { text: 'That is the one.', tone: 'calm' },
  { text: 'You got it.', tone: 'bright' },
  { text: 'Just right.', tone: 'calm' },
  { text: 'Yes, that is it.', tone: 'bright' },
  { text: 'Beautifully said.', tone: 'bright' },
  { text: 'You found the word.', tone: 'calm' },
  { text: 'Spot on.', tone: 'bright' },
  { text: 'That was lovely and clear.', tone: 'calm' },
  { text: 'Pip heard you.', tone: 'calm' },
  { text: 'Wonderful speaking.', tone: 'bright' },
  { text: 'Well done.', tone: 'calm' },
  { text: 'That is the word.', tone: 'calm' },
  { text: 'Bravo.', tone: 'bright' },
  { text: 'Marvellous.', tone: 'bright' },
  { text: 'Clear as a bell.', tone: 'bright' },
  { text: 'Straight to it.', tone: 'calm' },
  { text: 'Excellent.', tone: 'bright' },
  { text: 'Splendid.', tone: 'bright' },
  { text: 'You are speaking so well.', tone: 'calm' },
  { text: 'Right on the mark.', tone: 'bright' },
  { text: 'Terrific.', tone: 'bright' },
  { text: 'You told Pip exactly.', tone: 'calm' },
  { text: 'Super speaking.', tone: 'bright' },
  { text: 'That is the one Pip wanted.', tone: 'calm' },
  { text: 'Now that is talking.', tone: 'bright' },
  { text: 'Fantastic.', tone: 'bright' },
  { text: 'Just like that.', tone: 'calm' },
  { text: 'You made it happen.', tone: 'bright' },
  { text: 'Your voice did that.', tone: 'bright' },
  { text: 'Brilliant.', tone: 'bright' },
  { text: 'Nicely done.', tone: 'calm' },
  { text: 'That is it exactly.', tone: 'bright' },
  { text: 'Good speaking.', tone: 'calm' },
  { text: 'You said it so clearly.', tone: 'calm' },
  { text: 'Lovely work.', tone: 'calm' },
  { text: 'Pip is so pleased.', tone: 'bright' },
  { text: 'That is right.', tone: 'calm' },
  { text: 'Nice.', tone: 'calm' },
  { text: 'Good.', tone: 'calm' },
  { text: 'That works.', tone: 'calm' },
  { text: 'Yes, that one.', tone: 'calm' },
  { text: 'Mm, that is it.', tone: 'calm' },
  { text: 'Good listening.', tone: 'calm' },
  { text: 'Thank you.', tone: 'calm' },
  { text: 'There it is.', tone: 'calm' },
  { text: 'Steady and clear.', tone: 'calm' },
  { text: 'Just so.', tone: 'calm' },
  { text: 'You have it.', tone: 'calm' },
  { text: 'Quite right.', tone: 'calm' },
  { text: 'Very good.', tone: 'calm' },
  { text: 'Nicely said.', tone: 'calm' },
  { text: 'Right you are.', tone: 'bright' },
  { text: 'Good work.', tone: 'calm' },
  { text: 'That did it.', tone: 'bright' },
  { text: 'That was a strong voice.', tone: 'bright' },
  { text: 'What a clear voice.', tone: 'calm' },
  { text: 'You worked that out.', tone: 'calm' },
  { text: 'You remembered it.', tone: 'calm' },
  { text: 'You knew that one.', tone: 'calm' },
  { text: 'You said the whole thing.', tone: 'bright' },
  { text: 'Both words, beautifully.', tone: 'bright' },
  { text: 'Two words together.', tone: 'calm' },
  { text: 'You asked, and it came.', tone: 'calm' },
  { text: 'Pip understood you.', tone: 'calm' },
  { text: 'Pip knew just what you meant.', tone: 'calm' },
  { text: 'Message received.', tone: 'bright' },
  { text: 'Perfectly clear.', tone: 'bright' },
  { text: 'Crisp and clear.', tone: 'bright' },
  { text: 'That came easily.', tone: 'calm' },
  { text: 'You are getting quick.', tone: 'bright' },
  { text: 'You are doing so well.', tone: 'calm' },
  { text: 'Keep going like that.', tone: 'calm' },
  { text: 'That is the way.', tone: 'bright' },
  { text: 'Just what Pip hoped.', tone: 'calm' },
  { text: 'Exactly what Pip wanted.', tone: 'bright' },
  { text: 'A perfect answer.', tone: 'bright' },
  { text: 'Beautiful.', tone: 'calm' },
  { text: 'Delightful.', tone: 'bright' },
  { text: 'Superb.', tone: 'bright' },
  { text: 'First class.', tone: 'bright' },
  { text: 'Top marks.', tone: 'bright' },
  { text: 'Excellent speaking.', tone: 'bright' },
  { text: 'A lovely clear word.', tone: 'calm' },
  { text: 'Pip is smiling.', tone: 'calm' },
  { text: 'That made Pip happy.', tone: 'calm' },
  { text: 'You have made Pip’s day.', tone: 'bright' },
  { text: 'So clear.', tone: 'calm' },
  { text: 'Very nicely said.', tone: 'calm' },
  { text: 'Yes indeed.', tone: 'bright' },
  { text: 'That is correct.', tone: 'calm' },
  { text: 'Quite so.', tone: 'calm' },
  { text: 'Just the word Pip needed.', tone: 'calm' },
  { text: 'Pip could hear every sound.', tone: 'calm' },
  { text: 'Every sound in the right place.', tone: 'calm' },
  { text: 'Nice and steady.', tone: 'calm' },
  { text: 'Calm and clear.', tone: 'calm' },
  { text: 'That was gentle and clear.', tone: 'calm' },
  { text: 'Softly said, and just right.', tone: 'calm' },
  { text: 'Well spoken.', tone: 'calm' },
  { text: 'Good going.', tone: 'bright' },
  { text: 'Onward.', tone: 'bright' },
  { text: 'Look at that.', tone: 'bright' },
  { text: 'Would you look at that.', tone: 'bright' },
  { text: 'Off we go.', tone: 'bright' },
  { text: 'Here comes another.', tone: 'calm' },
  { text: 'You are speaking beautifully today.', tone: 'calm' },
  { text: 'That is just what it is.', tone: 'calm' },
  { text: 'Good voice.', tone: 'calm' },
  { text: 'Wonderful.', tone: 'bright' },
  { text: 'Lovely.', tone: 'calm' },
];

/** The child made a sound but no word was recognised. This is Phase 1 component B
 * succeeding, and it is the only pool allowed to answer a trial with no transcript. */
export const ENCOURAGEMENT = [
  { text: 'Pip heard your voice.', tone: 'calm' },
  { text: 'There is your voice. Pip likes that.', tone: 'calm' },
  { text: 'Pip is listening. Keep going.', tone: 'calm' },
  { text: 'Pip heard a sound. Try the word with Pip.', tone: 'calm' },
  { text: 'That was your voice. Once more.', tone: 'calm' },
  { text: 'Pip is right here, listening.', tone: 'calm' },
  { text: 'Good. Now the word.', tone: 'calm' },
  { text: 'Let us say it together.', tone: 'calm' },
  { text: 'One more time.', tone: 'calm' },
  { text: 'Take your time.', tone: 'calm' },
  { text: 'Pip will wait for you.', tone: 'calm' },
  { text: 'Your voice is doing something. Keep going.', tone: 'calm' },
  { text: 'Pip likes hearing you.', tone: 'calm' },
  { text: 'Nearly. Have another go.', tone: 'calm' },
];

/** A word came back, but not the one the level asked for. Never "wrong". */
export const NEARLY = [
  { text: 'Pip heard you. Let us find the right one.', tone: 'calm' },
  { text: 'Close. Listen once more.', tone: 'calm' },
  { text: 'Almost. Pip will help.', tone: 'calm' },
  { text: 'Good try. Here is a clue.', tone: 'calm' },
  { text: 'Let us try that one again.', tone: 'calm' },
  { text: 'Pip will say it first this time.', tone: 'calm' },
  { text: 'Nearly there.', tone: 'calm' },
];

/** Tries are used up. Pip does it with them; nobody has failed. */
export const ASSIST = [
  { text: 'Let us do this one together.', tone: 'calm' },
  { text: 'Pip will help with this one.', tone: 'calm' },
  { text: 'This one is tricky. Pip will take it.', tone: 'calm' },
  { text: 'Together, then. Ready?', tone: 'calm' },
];

/**
 * One pool from the platform's and a game's own, in that order, with any line
 * a game happens to duplicate dropped rather than doubled - a duplicate would
 * be twice as likely to come up, which is the opposite of the point.
 *
 * @param {Line[]} shared
 * @param {Line[]} [own]
 * @returns {Line[]}
 */
export function mergePools(shared, own = []) {
  const seen = new Set();
  const out = [];
  for (const line of [...shared, ...own]) {
    if (seen.has(line.text)) continue;
    seen.add(line.text);
    out.push(line);
  }
  return out;
}

export default { PRAISE, ENCOURAGEMENT, NEARLY, ASSIST, mergePools };
